import { useState, useCallback } from 'react';

// Undo/redo history over a single piece of state (here, the array of marks).
//
// `set(next)` records a new committed entry — the previous value is pushed onto
// the undo stack and the redo stack is cleared. `update(next)` mutates the
// current value WITHOUT creating a history entry; use it for in-progress drags
// (e.g. extending a freehand stroke) and call `commit()` once the action is
// finished so the whole gesture becomes a single undo step.
export const useHistory = (initialPresent) => {
  const [past, setPast] = useState([]);
  const [present, setPresent] = useState(initialPresent);
  const [future, setFuture] = useState([]);

  // Commit a new value, snapshotting the current one onto the undo stack.
  const set = useCallback((next) => {
    setPresent((current) => {
      const resolved = typeof next === 'function' ? next(current) : next;
      if (resolved === current) return current;
      setPast((p) => [...p, current]);
      setFuture([]);
      return resolved;
    });
  }, []);

  // Update the present value without touching history (for live drag previews).
  const update = useCallback((next) => {
    setPresent((current) => (typeof next === 'function' ? next(current) : next));
  }, []);

  // Push the current value onto the undo stack as the start of a committed
  // gesture. Pass the value that existed BEFORE the gesture began.
  const commit = useCallback((before) => {
    setPast((p) => [...p, before]);
    setFuture([]);
  }, []);

  const undo = useCallback(() => {
    setPast((p) => {
      if (p.length === 0) return p;
      const previous = p[p.length - 1];
      setPresent((current) => {
        setFuture((f) => [current, ...f]);
        return previous;
      });
      return p.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (f.length === 0) return f;
      const next = f[0];
      setPresent((current) => {
        setPast((p) => [...p, current]);
        return next;
      });
      return f.slice(1);
    });
  }, []);

  // Reset history entirely (e.g. when a new image is loaded).
  const reset = useCallback((value) => {
    setPast([]);
    setFuture([]);
    setPresent(value);
  }, []);

  return {
    present,
    set,
    update,
    commit,
    undo,
    redo,
    reset,
    canUndo: past.length > 0,
    canRedo: future.length > 0,
  };
};
